/**
 * KAN-862 — per-USER "already celebrated" receipts for the subscribe celebration. Same shape as
 * `../company/companyAccessGreetingReceipts.js` (a per-user, per-tenant local map + write-through via
 * `upsertNoticeReceipts` + MONOTONIC merge from the server through `NoticeReceiptsSync`), so a reload, a second
 * tab or a second device never plays it twice.
 *
 * notice_key = `subscribe_celebrated:<nonce>` — a random id minted when the checkout STARTS (no name, email or
 * date). A resubscribe starts a new checkout, so it has a new nonce and celebrates again; the same nonce never
 * replays. Written when the celebration is SHOWN.
 *
 * Also holds the device-local checkout marker `{ nonce, wasSubscribedBefore, startedAt }`, written the moment we
 * leave for Stripe. It is NOT a receipt (never synced). No marker = no celebration (an owner who has simply been
 * subscribed for months is never greeted), and `wasSubscribedBefore` (the status BEFORE checkout; the server's
 * `hasPriorSubscription` is always true afterwards) decides "Welcome back".
 */
import { upsertNoticeReceipts } from '../../services/noticeReceipts';
import { isNewerIso, maxIso } from '../notices/noticeReceiptMath';
import { tenantIdForNoticeWrite } from '../notices/noticeReceiptsSession';
import { SUBSCRIBE_CELEBRATED_PREFIX, clearSubscribePending, subscribeMarkerChanges } from './subscribeCheckoutMarker';

export {
  SUBSCRIBE_CELEBRATED_PREFIX, SUBSCRIBE_PENDING_PREFIX, SUBSCRIBE_PENDING_TTL_MS, subscribePendingKey, markSubscribeCheckoutStarted, readSubscribeMarker, clearSubscribePending,
} from './subscribeCheckoutMarker';

const PREFIX = 'subscribe_celebrated:';
const MAX_ENTRIES = 20;

/** The notice_key for a checkout marker; null without one. */
export function subscribeCelebrationNoticeKey(marker) {
  return marker?.nonce ? `${PREFIX}${marker.nonce}` : null;
}

export function ownsSubscribeCelebrationNoticeKey(noticeKey) {
  return typeof noticeKey === 'string' && noticeKey.startsWith(PREFIX);
}

export const subscribeCelebratedKey = (userId, tenantId) => `${SUBSCRIBE_CELEBRATED_PREFIX}${tenantId || 'tenant'}:${userId || 'anon'}`;

const listeners = new Set();
export function subscribeCelebrationReceiptChanges(fn) {
  listeners.add(fn);
  const offMarker = subscribeMarkerChanges(fn);
  return () => { listeners.delete(fn); offMarker(); };
}
function notify() {
  listeners.forEach((fn) => { try { fn(); } catch (_e) { /* never break the others */ } });
}

function capNewest(map) {
  const keep = Object.keys(map).sort((a, b) => Date.parse(map[b]) - Date.parse(map[a])).slice(0, MAX_ENTRIES);
  const out = {};
  keep.forEach((k) => { out[k] = map[k]; });
  return out;
}

/** `{ noticeKey: isoInstant }`. No user id -> nothing. Unreadable -> empty. */
export function readSubscribeCelebrated(userId, tenantId, storage) {
  if (!userId) return {};
  try {
    const raw = (storage || window.localStorage).getItem(subscribeCelebratedKey(userId, tenantId));
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out = {};
    Object.keys(parsed).forEach((k) => {
      if (!ownsSubscribeCelebrationNoticeKey(k) || Number.isNaN(Date.parse(parsed[k]))) return;
      out[k] = new Date(Date.parse(parsed[k])).toISOString();
    });
    return out;
  } catch {
    return {};
  }
}

export function hasCelebratedSubscribe(userId, tenantId, noticeKey, storage) {
  return Boolean(noticeKey) && Boolean(readSubscribeCelebrated(userId, tenantId, storage)[noticeKey]);
}

function writeMap(userId, tenantId, next, storage) {
  try {
    (storage || window.localStorage).setItem(subscribeCelebratedKey(userId, tenantId), JSON.stringify(capNewest(next)));
  } catch {
    /* best-effort local cache */
  }
}

/** Marks the subscription celebrated (max, never `=`), writes through to the server, clears the pending marker. */
export function markSubscribeCelebrated(userId, tenantId, noticeKey, storage, nowMs = Date.now(), opts = {}) {
  if (!userId || !tenantId || !ownsSubscribeCelebrationNoticeKey(noticeKey)) return;
  const current = readSubscribeCelebrated(userId, tenantId, storage);
  const stamp = maxIso(current[noticeKey], new Date(nowMs).toISOString());
  writeMap(userId, tenantId, { ...current, [noticeKey]: stamp }, storage);
  if (!opts.skipSync && tenantIdForNoticeWrite(userId) === tenantId) {
    upsertNoticeReceipts({ tenantId, userId, rows: [{ notice_key: noticeKey, announced_at: stamp }] });
  }
  clearSubscribePending(userId, tenantId, storage);
  notify();
}

/** Server rows -> local map, MONOTONIC. */
export function mergeSubscribeCelebratedFromServer(userId, tenantId, rows, storage) {
  if (!userId || !Array.isArray(rows)) return;
  let merged = readSubscribeCelebrated(userId, tenantId, storage);
  let changed = false;
  rows.forEach((row) => {
    if (!ownsSubscribeCelebrationNoticeKey(row?.notice_key) || !row.announced_at) return;
    if (!isNewerIso(row.announced_at, merged[row.notice_key])) return;
    merged = { ...merged, [row.notice_key]: row.announced_at };
    changed = true;
  });
  if (!changed) return;
  writeMap(userId, tenantId, merged, storage);
  notify();
}

/** Snapshot for the one-time post-hydration upload. */
export function collectSubscribeCelebratedForUpload(userId, tenantId, storage) {
  const map = readSubscribeCelebrated(userId, tenantId, storage);
  return Object.keys(map).map((k) => ({ notice_key: k, announced_at: map[k] }));
}
